import 'intersection-observer';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { JuloProvider } from '@julo-ui/react';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';
import App from './App';
import AppSnackbar from './components/AppSnackbar';
import LoadingOverlay from './components/LoadingOverlay';
import StorageGuard from 'components/StorageGuard';

import { SnackbarProvider } from '@new-components/elements';
import { queryClient } from '@utils/react-query';
import { ProfileProvider } from '@hooks/use-profile';
import { OnlineManagerProvider } from '@hooks/use-online-manager';
import GlobalError from '@new-components/global-error';

import GlobalStyle from './styles/app';

import { APP_ENV } from 'configs';

if (['staging', 'uat', 'production'].includes(APP_ENV)) {
  Sentry.init({
    dsn: 'https://9eff0e0e5f87427aae39fc9ab6d113b2@o54883.ingest.sentry.io/5647997',
    tracesSampleRate: 0.3,
    environment: APP_ENV,
    ignoreErrors: [
      /Loading chunk/,
      /Network Error/,
      /Unexpected token '<'/,
      /AndroidJs.onReceiveOtherMedia is not a function/,
      /getErrorPageVideo/,
    ],
  });
}

const container = document.getElementById('root');
const root = createRoot(container as Element);
const muiTheme = createTheme();

root.render(
  <HelmetProvider>
    <BrowserRouter>
      <JuloProvider>
        <MUIThemeProvider theme={muiTheme}>
          <StorageGuard>
            <LoadingOverlay />
            <QueryClientProvider client={queryClient}>
              <SnackbarProvider>
                <OnlineManagerProvider>
                  <ProfileProvider>
                    <App />
                    <GlobalError />
                  </ProfileProvider>
                </OnlineManagerProvider>
                {process.env.NODE_ENV === 'development' && (
                  <ReactQueryDevtools />
                )}
              </SnackbarProvider>
            </QueryClientProvider>
            <AppSnackbar />
          </StorageGuard>
        </MUIThemeProvider>
      </JuloProvider>
    </BrowserRouter>
    <GlobalStyle />
  </HelmetProvider>,
);
